/**
 * Turning a document's stored image watermarks back into something drawable
 * (VE-24693, extracted for VE-31006).
 *
 * The asset stores a mark's COLOUR BASE and its black-&-white / tint choices,
 * never the drawn form — that is a pure function of the two, and persisting it
 * as well doubled the asset and gave the export a copy that could fall out of
 * step with the flags. So every reader that needs to DRAW a stored mark has to
 * rebuild it, and this is where that happens.
 *
 * IT RETURNS RATHER THAN DISPATCHES, which is the whole reason it is a file of
 * its own. `openDocument` dispatches the result into the session, because an
 * open editor has a session to put it in. A headless burn (VE-31006, Redact
 * All) has no session on screen and must not create one — it builds a snapshot
 * off to the side and renders from that. Both need the same bytes by the same
 * rules, and a second copy of those rules is how one path ends up releasing a
 * document the other would not have.
 *
 * `hydrateProjectWatermarkImages` is the sibling of this for the merged
 * document's own record, and has the same shape for the same reason.
 *
 * BEST EFFORT, AND IT SAYS WHAT IT LOST. A mark that cannot be rebuilt keeps
 * its `imageAssetId` — a network fault reads exactly like a deleted asset from
 * here, and clearing the id would turn one bad request into permanent loss on
 * the next save — and its instance id comes back in `failedInstanceIds` so the
 * caller can report it. Nothing downstream can infer the failure on its own:
 * `toBurnInWatermark` drops an image mark with no drawn form, so an unreported
 * failure is a document that exports without the marking it claims to carry.
 */
import { call } from 'typed-redux-saga/macro';

import { allowedRasterContainer } from '@redact-modules/projects/projectRedactionAsset';
import type { TDOId } from '@common-modules/universal/models/Brands';
import { serviceLoadWatermarkImageAsset } from '../services';
import { deriveWatermarkImage } from '../watermarkImage';
import type { AppliedWatermark } from '../models';

/** One rebuilt mark, in the shape `HYDRATE_WATERMARK_IMAGES` takes. */
export interface HydratedWatermarkImage {
  readonly instanceId: string;
  readonly imageDataUrl: string;
  readonly imageBaseDataUrl?: string;
}

export interface WatermarkHydrationResult {
  readonly images: readonly HydratedWatermarkImage[];
  /**
   * Marks whose artwork this pass could not rebuild. Ids, not a count: the
   * panel has to name the rows it marks, and a number cannot (VE-28770).
   */
  readonly failedInstanceIds: readonly string[];
}

export function* hydrateDocumentWatermarkImages(args: {
  /** The document these marks belong to — the default home for their rasters. */
  readonly tdoId: TDOId;
  readonly marks: readonly AppliedWatermark[];
  /**
   * The merged document governing this file, where a set-wide mark's raster
   * lives (VE-30104), or null. The ONLY other container a mark here may
   * legitimately name.
   */
  readonly watermarkProjectTdoId: TDOId | null;
  readonly token: string;
  readonly cfg: {
    readonly apiRoot: string;
    readonly graphQLEndpoint: string;
    readonly veritoneAppId: string;
  };
}) {
  const { tdoId, marks, watermarkProjectTdoId, token, cfg } = args;
  const images: HydratedWatermarkImage[] = [];
  const failedInstanceIds: string[] = [];
  // Since VE-28770 every mark that survives `watermarksFromAssetBody` carries
  // a raster or a pointer to one, so a failure below always means artwork that
  // should have been readable and was not.
  const toHydrate = marks.filter((wm) => wm.type === 'image');
  for (const wm of toHydrate) {
    const rasterContainer = allowedRasterContainer({
      recorded: wm.imageAssetContainerId,
      fallback: tdoId,
      allowed: watermarkProjectTdoId ? [watermarkProjectTdoId] : [],
    });
    // Null ONLY when the mark named a container this reader does not hold — a
    // mark naming nowhere gets this document, which is where every pre-field
    // raster went. Both the asset id and the container come out of the same
    // org-writable body, so honouring the body's word would have the loader's
    // own container check compare the body against itself: anyone able to
    // write it could name an asset in any TDO and have a reviewer's token
    // fetch it onto a released page. Refused here rather than fetched.
    if (!rasterContainer) {
      failedInstanceIds.push(wm.instanceId);
      console.warn('Watermark names a raster container outside this document');
      continue;
    }
    try {
      // Format 1.3.0 keeps the raster in its own asset; a 1.2.0 body still
      // carries its base inline and needs no fetch. Once per mark either way —
      // later tint and grayscale changes re-derive from the base in memory,
      // and burn-in reuses the drawn form rather than fetching at export time.
      const base = wm.imageBaseDataUrl
        ? wm.imageBaseDataUrl
        : wm.imageAssetId
          ? yield* call(
              serviceLoadWatermarkImageAsset,
              wm.imageAssetId,
              rasterContainer,
              token,
              cfg.apiRoot,
              cfg.graphQLEndpoint,
              cfg.veritoneAppId
            )
          : // REACHED, and by design — do not delete it. A mark written with
            // nothing at all is dropped at `watermarksFromAssetBody`, but a
            // mark whose stored artwork this client REFUSED is deliberately
            // kept there and arrives with both fields nulled by the sanitizer:
            // a non-PNG, an oversized PNG, an `https://` source — any of which
            // the public API can write. That row is supposed to be counted and
            // reported, and this fallback is how it reaches `!base` to be.
            null;
      if (!base) {
        failedInstanceIds.push(wm.instanceId);
        continue;
      }
      const drawn = yield* call(deriveWatermarkImage, base, {
        grayscale: wm.grayscale,
        color: wm.color,
      });
      images.push({
        instanceId: wm.instanceId,
        imageDataUrl: drawn,
        imageBaseDataUrl: base,
      });
    } catch (err) {
      failedInstanceIds.push(wm.instanceId);
      console.warn('Watermark image could not be restored', err);
    }
  }
  return { images, failedInstanceIds };
}
